import { System } from "@models/System";
import { StateTree, defineStore } from "pinia";
import { User } from "@models/User";
import { LoginProfile } from "@models/LoginProfile";
import UserDataService from "@/services/UserDataService";
import SystemDataService from "@/services/SystemDataService";
import * as Sentry from "@sentry/vue";
import useDate from "@/modules/useDate";
import Config from "@/config/Config";
import { useLocalStorage } from "@vueuse/core";

const { formatISODatetime } = useDate();
const ACES_CACHE_NAME = "aces-cache";
const REDIRECT_URL_KEY = "/redirect-url";
const defaultUser: User = {
  id: "",
  displayName: null,
  email: null,
  phoneNumber: null,
  termsOfUse: null,
  emailVerified: false,
  phoneNumberVerified: false,
  sentVerifyEmail: false,
  reviewedApp: false,
  completedEvent: false,
} as unknown as User;

export const useUserProfileStore = defineStore("userProfile", {
  state: () => {
    const userProfile = {
      profile: { ...defaultUser },
      loginProfile: null,
      isUserAuthenticated: false,
      checkUserCreated: false,
      redirectUrl: "",
      verifyEmail: false,
      isSupportAdmin: false,
      verifyPhone: false,
      isInitialized: false,
      leaderboardViewMine: false,
    };
    return { ...userProfile };
  },
  actions: {
    setRedirectUrl(url: string): void {
      if (
        url === null ||
        url === undefined ||
        url.startsWith("/?v") ||
        url.toLowerCase().startsWith("/signout") ||
        url.toLowerCase().startsWith("/login") ||
        url.toLowerCase().startsWith("/signup")
      ) {
        return;
      }

      // Store in cache for browser to app communication
      if ("caches" in window) {
        window.caches
          .open(ACES_CACHE_NAME)
          .then((cache) =>
            cache.put(REDIRECT_URL_KEY, new Response(JSON.stringify({ redirectUrl: url })))
          );
      }
      this.redirectUrl = url;
    },
    setCompetedEvent(completed: boolean): void {
      this.profile.completedEvent = completed;
      UserDataService.save(this.profile);
    },
    setReviewedApp(): void {
      this.profile.reviewAppStatus = "Reviewed";
      UserDataService.save(this.profile);
    },
    setRemindReviewApp(): void {
      this.profile.reviewAppStatus = "NotReviewed";
      const reviewDate = new Date();
      reviewDate.setTime(reviewDate.getTime() + 15 * 24 * 60 * 60 * 1000);
      this.profile.reviewAfter = formatISODatetime(reviewDate);
      UserDataService.save(this.profile);
    },
    setNeverReviewApp(): void {
      this.profile.reviewAppStatus = "NeverReview";
      UserDataService.save(this.profile);
    },
    setVerifyEmail(verify: boolean): void {
      this.verifyEmail = verify;
    },
    setSentVerifyEmail(sent: boolean): void {
      this.profile.sentVerifyEmail = sent;
      this.save(this.profile);
    },
    smsOptOut(): void {
      this.profile.smsOptIn = false;
      UserDataService.save(this.profile);
    },
    smsOptIn(): void {
      this.profile.smsOptIn = true;
      UserDataService.save(this.profile);
    },
    setDisplayName(displayName: string): void {
      this.profile.displayName = displayName;
    },
    setPhoneNumber(phoneNumber: string): void {
      this.verifyPhone = true;
      this.profile.phoneNumber = phoneNumber;
    },
    setTermsOfService(accept: boolean): void {
      this.profile.acceptedTermsOfUse = accept;
      this.profile.acceptedTermsOfUseDate = accept ? formatISODatetime(new Date()) : "";
    },
    setPrivacyPolicy(accept: boolean): void {
      this.profile.acceptedPrivacyPolicy = accept;
      this.profile.acceptedPrivacyPolicyDate = accept ? formatISODatetime(new Date()) : "";
    },
    async loadRedirectUrlFromCache(): Promise<void> {
      // Get from cache for browser to app communication
      if ("caches" in window) {
        const cache = await caches.open(ACES_CACHE_NAME);
        const cacheMatch = await cache.match(REDIRECT_URL_KEY);
        const body = await cacheMatch?.text();
        const url = body ? JSON.parse(body)?.redirectUrl ?? this.redirectUrl : this.redirectUrl;
        this.redirectUrl = url;
      }
    },
    async syncProfileWithDb(): Promise<void> {
      try {
        if (this.profile.id) {
          // Set userAgent and appVersion before syncing
          this.profile.userAgent = navigator.userAgent;
          this.profile.appVersion = `v2.${Config.buildNumber}`;

          let user = await UserDataService.get(this.profile.id);
          if (!user) {
            await UserDataService.save(this.profile);
          } else if (user) {
            user.email = this.profile.email ?? user.email;
            user.phoneNumber = this.profile.phoneNumber ?? user.phoneNumber;
            user.userAgent = this.profile.userAgent;
            user.appVersion = this.profile.appVersion;
            this.profile.phoneNumber = user.phoneNumber;
            this.profile.sentVerifyEmail = user.sentVerifyEmail;
            this.profile.phoneNumberVerified = user.phoneNumberVerified;
            this.profile.displayName = user.displayName;
            await UserDataService.save(user);
            this.profile = user;
            this.checkUserCreated = true;
          } else {
            this.profile = {
              email: this.profile.email,
            } as unknown as User;
          }
          this.isInitialized = true;
        }
      } catch (error) {
        console.log("Syncing profile", error);
      }
    },
    async requestInformation(): Promise<void> {
      this.profile.requestInformation = true;
      await UserDataService.save(this.profile);
    },
    async checkSupportAdmin(): Promise<void> {
      const system = (await SystemDataService.getActive()) as System;
      if (system?.supportAdmins && system.supportAdmins.length > 0) {
        system.supportAdmins.forEach((admin: string) => {
          if (admin === this.profile.email) {
            this.isSupportAdmin = true;
          }
        });
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async setLoginProfile(loginProfile: any): Promise<void> {
      if (loginProfile) {
        Sentry.setUser({ email: loginProfile.email, id: loginProfile.uid });
        this.loginProfile = loginProfile;

        this.profile = {
          ...this.profile,
          id: loginProfile.uid,
          displayName: loginProfile.displayName ?? this.profile.displayName,
          email: loginProfile.email ?? this.profile.email,
          phoneNumber: loginProfile.phoneNumber ?? this.profile.phoneNumber,
          emailVerified: loginProfile.emailVerified,
        };

        if (
          !this.profile.displayName &&
          loginProfile.providerData.some((x: { displayName: string }) => x.displayName)
        ) {
          this.profile.displayName = loginProfile.providerData.find(
            (x: { displayName: string }) => x.displayName
          )?.displayName;
        }

        this.checkSupportAdmin();
        await this.syncProfileWithDb();
      } else {
        Sentry.setUser(null);
        this.profile = { ...defaultUser };
        this.loginProfile = null;
        this.isInitialized = false;
      }
    },
    async save(profile: User): Promise<void> {
      if (profile) {
        this.verifyPhone = profile.phoneNumberVerified ? false : this.verifyPhone;
        await UserDataService.save(profile);
      }
    },
    setIsAuthenticated(auth: boolean) {
      if (this.isUserAuthenticated !== auth) {
        this.isUserAuthenticated = auth;
      }
    },
    async deleteAccount(): Promise<void> {
      await UserDataService.delete(this.profile.id);
    },
    setLeaderboardViewMine(leaderboardViewMine: boolean): void {
      this.leaderboardViewMine = leaderboardViewMine;
    },
  },
  getters: {
    isAuthenticated: (state) => state.isUserAuthenticated,
    shouldPromptReview: (state) => {
      const now = new Date();
      const reviewAfter = state.profile.reviewAfter
        ? new Date(state.profile.reviewAfter)
        : new Date();
      reviewAfter.setHours(0, 0, 0, 0);
      return (
        state.profile.completedEvent &&
        (state.profile.reviewAppStatus ?? "NotReviewed") === "NotReviewed" &&
        reviewAfter <= now
      );
    },
  },
  persist: {
    pick: [
      'profile',
      'loginProfile', 
      'isUserAuthenticated',
      'checkUserCreated',
      'redirectUrl',
      'verifyEmail',
      'isSupportAdmin',
      'verifyPhone',
      'leaderboardViewMine'
    ]
  }
});
